import { ControlledTextField, ControlNumberField } from 'components/Form';
import { ReadField } from 'components/ReadField';
import type { BimContractOtherStatusEnum } from 'gql/graphql';
import {
    ChapterEnum,
    ContractOtherStatusEnum,
    OtherCostCalculationTypeEnum,
    ProjectMethodologyEnum,
} from 'gql/graphql';
import type { TypeItem } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { OTHER_SECTION_OR_CHAPTER, TYPE_VARIANTS } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { Grid, GridItem, RadioGroup } from 'uikit';
import { formatNumberToRu } from 'utils';

import type React from 'react';
import { useCallback, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { OtherCostTypeSelect } from './OtherCostTypeSelect';

import Styles from './EditOtherForm.module.css';

const getItemLabel = (item: { label: any }) => item.label;

export const getOtherMethod = ({
    status,
    isGlobal,
}: {
    status?: BimContractOtherStatusEnum | ContractOtherStatusEnum;
    isGlobal?: boolean;
}) => {
    if (status && [ContractOtherStatusEnum.NewRecord, 'DRAFT'].includes(status) && !isGlobal) {
        return 'Вручную';
    }

    return isGlobal ? 'Из справочника методик расчета' : 'Из справочника договора';
};

interface IEditOtherCommon {
    control: Control<any>;
    isGlobal?: boolean;
    chapter: any;
    selectedType: TypeItem;
    selectedChapterOrSection: any;
    changeSelectedTypeHandler: (newType: TypeItem) => void;
    changeSelectedChapterOrSection: (value: string) => void;
    areFieldsDisabled: boolean;
    globalOtherName?: string;
    isChapterEditable: boolean;
    isCalculationTypeEditable: boolean;
    isNameFieldDisabled: boolean;
    isTooltipHidden?: boolean;
    isModalReadOnly?: boolean;
    otherStatus?: BimContractOtherStatusEnum | ContractOtherStatusEnum;
    methodology: ProjectMethodologyEnum;
    isOnlyTypeEditAvailable?: boolean;
    calculatingTypeValue?: number;
    isModify?: boolean;
    otherId: number;
    isCalculationModal?: boolean;
}

export const EditOtherCommon: React.FC<IEditOtherCommon> = (props) => {
    const {
        control,
        isGlobal,
        chapter,
        selectedType,
        changeSelectedTypeHandler,
        areFieldsDisabled,
        globalOtherName,
        isChapterEditable,
        isCalculationTypeEditable,
        isNameFieldDisabled,
        isOnlyTypeEditAvailable,
        selectedChapterOrSection,
        changeSelectedChapterOrSection,
        isTooltipHidden,
        otherStatus,
        isModalReadOnly,
        methodology,
        calculatingTypeValue,
        isModify,
        otherId,
        isCalculationModal = false,
    } = props;

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const methodValue = getOtherMethod({ isGlobal, status: otherStatus });
    const chapterValue = chapter === ChapterEnum.Chapter9 ? 'Глава 9' : 'Раздел "Лимиты Заказчика"';
    const isTypeFieldDisabled = !isGlobal;

    const handleChangeType = useCallback(({ value }) => changeSelectedTypeHandler(value), [changeSelectedTypeHandler]);
    const onSelectedChapterOrSectionChange = useCallback(
        ({ value }) => changeSelectedChapterOrSection(value),
        [changeSelectedChapterOrSection],
    );

    const isCostTypeFieldDisabled = useMemo(() => {
        if (isModify) {
            return false;
        }

        return areFieldsDisabled || isModalReadOnly || isGlobal;
    }, [isModify, areFieldsDisabled, isModalReadOnly, isGlobal]);

    const isDisabledTypeRadio = useMemo(
        () => !isCalculationTypeEditable || isGlobal || isModalReadOnly || isOnlyTypeEditAvailable,
        [isCalculationTypeEditable, isGlobal, isModalReadOnly, isOnlyTypeEditAvailable],
    );

    const { field } = useController({
        name: 'otherCostType',
        control,
    });

    return (
        <Grid>
            {(!isChapterEditable || isOnlyTypeEditAvailable) && !isCalculationModal && (
                <GridItem className={`${Styles.negativeM}`}>
                    <ReadField value={methodValue} label="Способ создания" />
                </GridItem>
            )}
            {(!isOnlyTypeEditAvailable || (isCalculationModal && !isGlobal)) &&
            (isChapterEditable || isTypeFieldDisabled) ? (
                <GridItem className={Styles.marginBlock}>
                    <ControlledTextField
                        name="name"
                        label="Наименование"
                        required
                        control={control}
                        type="textarea"
                        disabled={isNameFieldDisabled || areFieldsDisabled || isGlobal || isModalReadOnly}
                        rules={{
                            required: 'Обязательное поле',
                            maxLength: {
                                value: 255,
                                message: 'Максимальное количество символов – 255',
                            },
                        }}
                        isTooltipHidden={isTooltipHidden}
                    />
                </GridItem>
            ) : (
                <GridItem className={Styles.negativeM}>
                    <ReadField value={globalOtherName} label="Наименование" />
                </GridItem>
            )}
            {isCalculationModal && (
                <GridItem className={Styles.marginBlock}>
                    <ControlledTextField
                        name="key"
                        label="Код"
                        control={control}
                        type="textarea"
                        disabled={isModalReadOnly}
                        rules={{
                            maxLength: {
                                value: 255,
                                message: 'Максимальное количество символов – 255',
                            },
                        }}
                        isTooltipHidden={isTooltipHidden}
                        tooltipSize="l"
                    />
                </GridItem>
            )}

            {isUer && (
                <GridItem className={Styles.marginBlock}>
                    <ControlledTextField name="key" label="Код" control={control} type="text" placeholder="" disabled />
                </GridItem>
            )}

            {!isOnlyTypeEditAvailable ? (
                <GridItem className={Styles.marginBlock}>
                    <div className="TextField TextField_labelPosition_left TextField_size_s TextField_view_default TextField_width_full">
                        <div className="Text Text_lineHeight_m Text_size_s Text_view_secondary FieldLabel TextField-Label TextField-Label_labelPosition_left">
                            Способ расчета
                        </div>

                        {isDisabledTypeRadio ? (
                            <div className={Styles.textBlock}>{selectedType.label || ''}</div>
                        ) : (
                            <RadioGroup
                                name="type"
                                direction="row"
                                items={TYPE_VARIANTS}
                                value={selectedType}
                                className={Styles.RadioGroup}
                                getItemLabel={getItemLabel}
                                onChange={handleChangeType}
                            />
                        )}
                    </div>
                </GridItem>
            ) : (
                <GridItem className={Styles.negativeM}>
                    <ReadField value={selectedType.label} label="Способ расчета" />
                </GridItem>
            )}

            {selectedType.id === OtherCostCalculationTypeEnum.Percent &&
                (!isOnlyTypeEditAvailable || isCalculationModal ? (
                    <GridItem className={Styles.marginBlock}>
                        <ControlNumberField
                            name="calculatingTypeValue"
                            label="Величина %"
                            labelPosition="left"
                            control={control}
                            type="text"
                            size="s"
                            required
                            rules={{ required: 'Обязательное поле' }}
                            disabled={
                                areFieldsDisabled || isModalReadOnly || (isOnlyTypeEditAvailable && !isCalculationModal)
                            }
                            className={Styles.typeValueField}
                            isTooltipHidden={isTooltipHidden}
                            decimalScale={isUer ? 2 : 3}
                        />
                    </GridItem>
                ) : (
                    <GridItem className={Styles.negativeM}>
                        <ReadField value={formatNumberToRu(calculatingTypeValue, isUer ? 2 : 3)} label="Величина %" />
                    </GridItem>
                ))}

            {(!isChapterEditable || isOnlyTypeEditAvailable) && (
                <GridItem className={`${Styles.marginTop} ${Styles.negativeM}`}>
                    <ReadField value={chapterValue} label="Раздел" />
                </GridItem>
            )}
            {isChapterEditable && !isOnlyTypeEditAvailable && (
                <GridItem className={Styles.marginBlock}>
                    <div className="TextField TextField_labelPosition_left TextField_size_s TextField_view_default TextField_width_full">
                        <div className="Text Text_lineHeight_m Text_size_s Text_view_secondary FieldLabel TextField-Label TextField-Label_labelPosition_left">
                            Раздел
                        </div>
                        <RadioGroup
                            name="selectedChapterOrSection"
                            direction="row"
                            items={OTHER_SECTION_OR_CHAPTER}
                            value={selectedChapterOrSection}
                            getItemLabel={getItemLabel}
                            onChange={onSelectedChapterOrSectionChange}
                            className={Styles.RadioGroup}
                        />
                    </div>
                </GridItem>
            )}

            {!isCalculationModal ? (
                <OtherCostTypeSelect
                    isTooltipHidden={isTooltipHidden}
                    isDisabled={isCostTypeFieldDisabled}
                    control={control}
                    id={otherId}
                />
            ) : (
                <GridItem className={`${Styles.marginTop} ${Styles.negativeM}`}>
                    <ReadField value={field?.value?.name} label="Тип прочих" />
                </GridItem>
            )}
        </Grid>
    );
};

EditOtherCommon.displayName = 'EditOtherCommon';
